import { Box, CircularProgress, Stack, useTheme } from "@mui/material";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { PaginationModel } from "../components/common/CustomTable";
import FileUploader from "../components/common/FileUploader";
import useScreen from "../components/common/UseScreen";
import UploadedFilesTable from "../components/uploaded-files/UploadedFilesTable";
import { SnackbarMsg } from "../data/texts";
import { FileWithId } from "../models/uploaded-file";
import { EventLoppService } from "../services/event-loop.service";
import { FileService } from "../services/file.service";
import { UserService } from "../services/user.service";
import { showSnackbar } from "../store/system.store";

const UploadedFilesPage = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [files, setFiles] = useState<FileWithId[]>([]);
  const [pageNumber, setPageNumber] = useState(0);
  const [totalFiles, setTotalFiles] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const { isDesktop } = useScreen();

  useEffect(() => {
    fetchFiles();
  }, [pageNumber]);

  const onPaginationModelChange = (model: PaginationModel) => {
    setPageNumber(model.page + 1);
  };

  const fetchFiles = async () => {
    setLoadingNextTick(true);
    const files = await UserService.getUserFileList(pageNumber, 7);
    if (files?.list?.length) {
      setTotalFiles(files.total);

      const modifiedFiles: FileWithId[] = files?.list.map((file) => {
        return {
          id: file.id,
          createdAt: file.Created_Time,
          content: {
            name: file.File_Name,
            size: Number(file.Size),
            type: FileService.getFileTypeByName(file.File_Name),
          },
        };
      });

      setFiles([...modifiedFiles]);
    }
    setLoadingNextTick(false);
  };

  const setLoadingNextTick = (isLoading: boolean) => {
    EventLoppService.nextTick(() => {
      setIsLoading(isLoading);
    });
  };

  const onDeleteFile = async (file: FileWithId) => {
    setLoadingNextTick(true);
    await FileService.deleteFile(file.id);
    await fetchFiles();
    dispatch(showSnackbar({ msg: SnackbarMsg.SNACKBAR_DELETE }));
    setLoadingNextTick(false);
  };

  const onDeleteMultipleFiles = async (filesToDelete: FileWithId[]) => {
    setLoadingNextTick(true);
    const ids = filesToDelete.map((file) => file.id).join(",");
    await FileService.deleteFiles(ids);

    EventLoppService.nextTick(async () => {
      await fetchFiles();
      dispatch(showSnackbar({ msg: SnackbarMsg.SNACKBAR_DELETE }));
      setLoadingNextTick(false);
    }, 200);
  };

  const onUploadFile = async (filesUploaded: FileWithId[]) => {
    setLoadingNextTick(true);
    await FileService.uploadFiles(filesUploaded);
    await fetchFiles();
    dispatch(showSnackbar({ msg: SnackbarMsg.SNACKBAR_FILES_UPLOADED }));

    setLoadingNextTick(false);
  };
  return (
    <Box>
      {isLoading && (
        <CircularProgress
          sx={{
            position: "fixed",

            top: "50%",
            left: !isDesktop
              ? "45%"
              : `calc(38% + ${theme.custom.appDrawerWidth})`,
            zIndex: "1000",
          }}
        />
      )}
      <Stack
        sx={{
          gap: "20px",
          padding: "24px",
          background: theme.palette.background.default,
          position: "relative",
          ...(isLoading ? theme.customStyles.disabled : {}),
        }}
      >
        {files && (
          <Box sx={{ position: "relative", width: "100%" }}>
            <UploadedFilesTable
              stopLoading={() => setLoadingNextTick(false)}
              onDeleteMultipleFiles={onDeleteMultipleFiles}
              onDeleteFile={onDeleteFile}
              onActionClick={() => setIsLoading(true)}
              isLoading={isLoading}
              onPaginationModelChange={onPaginationModelChange}
              totalRowNumber={totalFiles}
              filesWithId={files}
            />
          </Box>
        )}
        <Box
          sx={{
            width: "100%",
            padding: "20px",
            height: "302px",
            background: "white",
            borderRadius: "6px",
            boxShadow: "0px 2px 6px 0px #4C4E641A",
          }}
        >
          <FileUploader
            onFilesSelected={onUploadFile}
            sx={{ height: "100%", borderRadius: "6px" }}
          />
        </Box>
      </Stack>
    </Box>
  );
};

export default UploadedFilesPage;
